import { useState } from 'react';
import { X, Telescope } from 'lucide-react';
import { useLang } from '../i18n';
import { describeAuthError } from '../hooks/useAuth';

const C = {
  bg: "#0d1117", surface: "#161b22", border: "#30363d",
  text: "#e6edf3", muted: "#8b949e", accent: "#3b82f6",
};

export function AuthModal({ onClose, onAuth, onGoogleAuth, mode: initMode = 'signin' }) {
  const [mode, setMode] = useState(initMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { lang } = useLang();

  const handleGoogle = async () => {
    if (!onGoogleAuth) return;
    setError(''); setSuccess(''); setGoogleLoading(true);
    // 弹窗式登录：主页面全程不导航，Promise 要等弹窗流程结束才 resolve
    // （成功/失败/用户手动关弹窗）。三种结果都要落地把 loading 状态收掉。
    const { error: err, cancelled } = await onGoogleAuth();
    setGoogleLoading(false);
    if (err) { setError(describeAuthError(err.message, lang)); return; }
    if (!cancelled) onClose(); // 成功：session 已在 useAuth 里就绪，直接关弹窗
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!email || !password) {
      setError(lang === 'en' ? 'Please enter email and password' : '请填写邮箱和密码');
      return;
    }
    if (password.length < 6) {
      setError(lang === 'en' ? 'Password must be at least 6 characters' : '密码至少 6 位');
      return;
    }
    setLoading(true);
    const { error: err } = await onAuth(mode, email, password);
    setLoading(false);
    if (err) { setError(err.message); return; }
    if (mode === 'signup') {
      setSuccess(lang === 'en' ? 'Registered! Check your email to confirm before signing in.' : '注册成功！请检查邮箱确认后登录。');
    } else {
      onClose();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, width: 360, position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: C.muted, cursor: 'pointer', display: 'flex' }}><X size={18} strokeWidth={2} /></button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4, color: C.accent }}><Telescope size={26} strokeWidth={2} /></div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>PrimaryLens</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
            {mode === 'signin'
              ? (lang === 'en' ? 'Sign in to your account' : '登录你的账户')
              : (lang === 'en' ? 'Create a new account' : '创建新账户')}
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', color: '#10b981', padding: 16 }}>{success}</div>
        ) : (
          <>
            {error && !showEmailForm && (
              <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12, textAlign: 'center' }}>{error}</div>
            )}
            {onGoogleAuth && (
              <>
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={googleLoading || loading}
                  style={{
                    width: '100%', background: '#fff', border: `1px solid ${C.border}`,
                    borderRadius: 6, padding: '10px 12px', color: '#1f2937',
                    fontSize: 14, fontWeight: 600, cursor: googleLoading ? 'not-allowed' : 'pointer',
                    opacity: googleLoading ? 0.7 : 1, marginBottom: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  {googleLoading
                    ? (lang === 'en' ? 'Redirecting...' : '跳转中...')
                    : (lang === 'en' ? 'Continue with Google' : '使用 Google 继续')}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 16px', color: C.muted, fontSize: 11 }}>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                  <span>{lang === 'en' ? 'or' : '或'}</span>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                </div>

                {!showEmailForm && (
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(true)}
                    style={{
                      width: '100%', background: 'transparent', border: `1px solid ${C.border}`,
                      borderRadius: 6, padding: '10px', color: C.text, fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {lang === 'en' ? 'Use email & password' : '使用邮箱和密码'}
                  </button>
                )}
              </>
            )}

            {(showEmailForm || !onGoogleAuth) && (
              <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 6 }}>{lang === 'en' ? 'Email' : '邮箱'}</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" autoFocus
                style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px', color: C.text, fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 6 }}>{lang === 'en' ? 'Password' : '密码'}</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••"
                style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px', color: C.text, fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <button type="submit" disabled={loading}
              style={{ width: '100%', background: C.accent, border: 'none', borderRadius: 6, padding: '10px', color: '#fff', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading
                ? (lang === 'en' ? 'Processing...' : '处理中...')
                : mode === 'signin'
                  ? (lang === 'en' ? 'Sign In' : '登录')
                  : (lang === 'en' ? 'Sign Up' : '注册')}
            </button>
              </form>
            )}
          </>
        )}

        {(showEmailForm || !onGoogleAuth) && !success && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: C.muted }}>
            {mode === 'signin'
              ? (lang === 'en' ? "Don't have an account?" : '没有账户？')
              : (lang === 'en' ? 'Already have an account?' : '已有账户？')}
            <button onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setSuccess(''); }}
              style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, marginLeft: 4 }}>
              {mode === 'signin'
                ? (lang === 'en' ? 'Sign Up' : '注册')
                : (lang === 'en' ? 'Sign In' : '登录')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
